/**
 * TrainingWikiPage.jsx · v2.7.64+E49 · Knowledge Center (WIKI)
 * /admin/training-center/wiki
 * ────────────────────────────────────────────────────────────
 *  • Agent-generated + custom how-to articles in numbered chapters
 *  • AI semantic (vector) search bar — meaning-based ranking via the
 *    backend's local BGE embedder (POST /admin/training/search)
 *  • Printable Manual Compiler — background job → downloadable PDF book
 *    with cover, automated TOC, numbered chapters and page footers
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, Download, FileText, Loader2, RefreshCw, Search, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import {
  AgentBadge, AudienceChip, TrainingHeader, TrainingMarkdown, TypeChip,
} from "./trainingShared";

export default function TrainingWikiPage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [reader, setReader] = useState(null);         // full article being read
  const [loading, setLoading] = useState(true);

  // ── semantic search ──
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);       // null = browse mode
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(null); // semantic | keyword_fallback
  const debounceRef = useRef(null);

  // ── manual compiler ──
  const [manualJob, setManualJob] = useState(null);
  const [compiling, setCompiling] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, m] = await Promise.all([
        adminApi.get("/admin/training/content?content_type=WIKI_ARTICLE"),
        adminApi.get("/admin/training/sections"),
        adminApi.get("/admin/training/manual/status"),
      ]);
      setArticles(a.data.items || []);
      setSections((s.data.sections || []).filter((x) => x.wiki_count > 0));
      setManualJob(m.data.job);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load the knowledge base");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Debounced semantic search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 3) { setResults(null); setSearchMode(null); return undefined; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await adminApi.post("/admin/training/search", { query: q, limit: 10 });
        setResults(data.results || []);
        setSearchMode(data.mode);
      } catch (_e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 450);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const openArticle = useCallback(async (slugOrId) => {
    try {
      const { data } = await adminApi.get(`/admin/training/content/${encodeURIComponent(slugOrId)}`);
      setReader(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (_e) {
      toast.error("Article failed to load");
    }
  }, []);

  const openResult = (r) => {
    if (r.content_type === "LMS_MODULE") {
      navigate("/admin/training-center/lms");
      return;
    }
    setQuery(""); setResults(null);
    openArticle(r.slug || r.id);
  };

  // ── PDF manual ──
  const jobBusy = manualJob && ["queued", "running"].includes(manualJob.status);
  useEffect(() => {
    if (!jobBusy) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await adminApi.get("/admin/training/manual/status");
        setManualJob(data.job);
        if (data.job && !["queued", "running"].includes(data.job.status)) {
          clearInterval(pollRef.current);
          if (data.job.status === "done") toast.success("Training manual compiled — ready to download");
          if (data.job.status === "error") toast.error("Manual compile failed");
        }
      } catch (_e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [jobBusy]);

  const compileManual = async () => {
    setCompiling(true);
    try {
      await adminApi.post("/admin/training/manual/compile");
      const { data } = await adminApi.get("/admin/training/manual/status");
      setManualJob(data.job);
      toast.info("Compiler started — building the PDF book in the background");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start the compiler");
    } finally {
      setCompiling(false);
    }
  };

  const downloadManual = async () => {
    try {
      const resp = await adminApi.get("/admin/training/manual/download", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = manualJob?.filename || "training-manual.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error("Download failed — compile the manual first");
    }
  };

  const bySection = useMemo(() => {
    const map = {};
    for (const a of articles) (map[a.section_group] ||= []).push(a);
    return map;
  }, [articles]);

  const visibleSections = selectedSection ? sections.filter((s) => s.section_group === selectedSection) : sections;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="training-wiki-page">
      <TrainingHeader
        Icon={BookOpen}
        title="Knowledge Center"
        subtitle="Company WIKI · step-by-step guides auto-generated from the platform + curated additions"
        right={
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
                    data-testid="wiki-refresh-btn" title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* ── Semantic search bar ── */}
        <div className="relative">
          <div className="flex items-center gap-3 bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 focus-within:border-amber-500/60 transition-colors">
            {searching
              ? <Loader2 className="h-5 w-5 text-amber-400 animate-spin" />
              : <Search className="h-5 w-5 text-zinc-500" />}
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search the knowledge base — try "refund a completed ride"'
              className="flex-1 bg-transparent outline-none text-sm text-zinc-100 placeholder:text-zinc-600"
              data-testid="wiki-search-input"
            />
            {query && (
              <button onClick={() => { setQuery(""); setResults(null); }}
                      className="text-zinc-500 hover:text-zinc-200" data-testid="wiki-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-yellow-300/90 bg-yellow-500/10 border border-yellow-500/30 rounded px-2 py-1">
              <Sparkles className="h-3 w-3" /> SMART SEARCH
            </span>
          </div>
        </div>

        {/* ── PDF Manual Compiler card ── */}
        <div className="flex items-center justify-between gap-4 flex-wrap bg-zinc-900/60 border border-zinc-800 rounded-xl px-4 py-3"
             data-testid="wiki-manual-card">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-amber-400" />
            <div>
              <div className="text-sm font-bold text-zinc-200">Printable Training Manual</div>
              <div className="text-[11px] text-zinc-500" data-testid="wiki-manual-status">
                {!manualJob && "Never compiled — build the PDF book with TOC + numbered chapters."}
                {manualJob?.status === "queued" && "Queued…"}
                {manualJob?.status === "running" && "Compiling chapters, TOC and pagination…"}
                {manualJob?.status === "done" &&
                  `Ready · ${manualJob.article_count} articles · ${(manualJob.size_bytes / 1024).toFixed(0)} KB · ${manualJob.filename}`}
                {manualJob?.status === "error" && `Failed: ${manualJob.error}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={compileManual} disabled={compiling || jobBusy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-amber-500/50 text-amber-200 text-xs font-bold hover:bg-amber-500/15 transition-colors disabled:opacity-50"
              data-testid="wiki-compile-btn">
              {jobBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              {jobBusy ? "Compiling…" : "Compile PDF Manual"}
            </button>
            {manualJob?.status === "done" && (
              <button onClick={downloadManual}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/50 text-emerald-200 text-xs font-bold hover:bg-emerald-500/25 transition-colors"
                data-testid="wiki-download-btn">
                <Download className="h-3.5 w-3.5" /> Download PDF
              </button>
            )}
          </div>
        </div>

        {/* ── Search results ── */}
        {results !== null && (
          <div className="space-y-2" data-testid="wiki-search-results">
            <div className="text-[11px] font-mono text-zinc-500 uppercase">
              {results.length} matches · ranked by {searchMode === "text" ? "relevance (weighted full-text)" : "keyword match"}
            </div>
            {results.length === 0 && !searching && (
              <div className="text-sm text-zinc-500 py-6 text-center" data-testid="wiki-search-empty">
                Nothing matched — try describing what you want to do.
              </div>
            )}
            {results.map((r) => (
              <button key={r.id} onClick={() => openResult(r)}
                className="w-full text-left bg-zinc-900/70 border border-zinc-800 hover:border-amber-500/40 rounded-lg px-4 py-3 transition-colors"
                data-testid={`wiki-result-${r.slug}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  {typeof r.score === "number" && (
                    <span className="text-[10px] font-mono text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 rounded px-1.5 py-0.5">
                      {(r.score * 100).toFixed(0)}%
                    </span>
                  )}
                  <span className="text-sm font-bold text-zinc-100">{r.title}</span>
                  <TypeChip type={r.content_type} />
                  <AudienceChip audience={r.target_audience} />
                  <span className="text-[10px] text-zinc-500">Ch.{r.chapter_number} · {r.section_group}</span>
                </div>
                <div className="text-xs text-zinc-400 mt-1">{r.snippet}</div>
              </button>
            ))}
          </div>
        )}

        {/* ── Reader ── */}
        {results === null && reader && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-6" data-testid="wiki-article-reader">
            <button onClick={() => setReader(null)}
              className="text-[11px] font-bold text-zinc-500 hover:text-amber-300 mb-4 transition-colors"
              data-testid="wiki-reader-back">
              ← All chapters
            </button>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[11px] font-mono text-amber-400/80">
                CHAPTER {reader.chapter_number} · {reader.section_group?.toUpperCase()}
              </span>
            </div>
            <h2 className="text-2xl font-black text-zinc-100 mb-2" data-testid="wiki-reader-title">{reader.title}</h2>
            <div className="flex items-center gap-2 flex-wrap mb-5">
              <AudienceChip audience={reader.target_audience} />
              {reader.is_agent_generated && <AgentBadge />}
              <span className="text-[10px] text-zinc-500">{reader.word_count} words</span>
              {reader.attachment_id && (
                <a className="text-[11px] text-sky-300 underline"
                   href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/training/attachments/${reader.attachment_id}`}
                   data-testid="wiki-reader-attachment">
                  📎 {reader.attachment_name || "attachment"}
                </a>
              )}
            </div>
            <TrainingMarkdown>{reader.body_content}</TrainingMarkdown>
          </div>
        )}

        {/* ── Browse: chapters + articles ── */}
        {results === null && !reader && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1 space-y-1" data-testid="wiki-chapter-nav">
              <div className="text-[11px] font-mono text-zinc-500 uppercase mb-2">Chapters</div>
              <button onClick={() => setSelectedSection(null)}
                className={`w-full text-left px-3 py-2 rounded-md text-xs font-bold transition-colors ${!selectedSection ? "bg-amber-400/10 text-amber-300 border border-amber-400/40" : "text-zinc-400 hover:bg-zinc-900 border border-transparent"}`}
                data-testid="wiki-chapter-all">
                All chapters
              </button>
              {sections.map((s) => (
                <button key={s.section_group}
                  onClick={() => setSelectedSection(s.section_group === selectedSection ? null : s.section_group)}
                  className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors border ${selectedSection === s.section_group ? "bg-amber-400/10 text-amber-300 border-amber-400/40" : "text-zinc-400 hover:bg-zinc-900 border-transparent"}`}
                  data-testid={`wiki-chapter-${s.chapter_number}`}>
                  <span className="font-mono text-[10px] opacity-60 mr-1.5">{s.chapter_number}.</span>
                  <span className="font-bold">{s.section_group}</span>
                  <span className="opacity-50 ml-1">({s.wiki_count})</span>
                </button>
              ))}
            </div>

            <div className="lg:col-span-3 space-y-6">
              {loading && <div className="text-zinc-500 text-sm py-8 text-center" data-testid="wiki-loading">Loading knowledge base…</div>}
              {!loading && visibleSections.map((s) => (
                <div key={s.section_group}>
                  <h3 className="text-sm font-black text-amber-300/90 uppercase tracking-wide mb-2">
                    Chapter {s.chapter_number} — {s.section_group}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(bySection[s.section_group] || []).map((a) => (
                      <button key={a.id} onClick={() => openArticle(a.slug || a.id)}
                        className="text-left bg-zinc-900/60 border border-zinc-800 hover:border-amber-500/40 rounded-lg px-4 py-3 transition-colors"
                        data-testid={`wiki-article-item-${a.slug}`}>
                        <div className="text-sm font-bold text-zinc-100">{a.title}</div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <AudienceChip audience={a.target_audience} />
                          {a.is_agent_generated && <AgentBadge />}
                          <span className="text-[10px] text-zinc-500">{a.word_count} words</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
